import { Delete } from 'lucide-react';

/**
 * Big-key number pad for a phone held in one hand behind a counter.
 *
 * `allowDecimal` turns on the point. It is off by default because most stock is
 * whole pieces and an accidental "1.2" cookers is a worse mistake than a missing
 * key -- it is only wanted for the things that genuinely divide, like brass by
 * the kilo or wire by the metre.
 */
export default function Numpad({
  value,
  onChange,
  onConfirm,
  maxLength = 10,
  allowDecimal = false,
}) {
  const handlePress = (num) => {
    if (value.length < maxLength) {
      onChange(value + num);
    }
  };

  const handleDecimal = () => {
    // One point only, and "0." rather than a bare "." so the display reads as a
    // number the whole way through being typed.
    if (value.includes('.') || value.length >= maxLength) return;
    onChange(value === '' ? '0.' : value + '.');
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <div className="p-4 bg-[#F0F0E8] rounded-2xl">
      {/* Display */}
      <div className="mb-4 p-4 bg-white rounded-xl text-center">
        <span className="text-4xl font-bold text-[#2D2D2D] tracking-wider">
          {value || '0'}
        </span>
      </div>

      {/* Numpad Grid */}
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <button
            key={num}
            onClick={() => handlePress(String(num))}
            className="numpad-btn"
            data-testid={`numpad-${num}`}
          >
            {num}
          </button>
        ))}

        {allowDecimal ? (
          <button
            onClick={handleDecimal}
            className="numpad-btn"
            data-testid="numpad-decimal"
          >
            .
          </button>
        ) : (
          <button
            onClick={handleClear}
            className="numpad-btn text-gray-500 text-base"
            data-testid="numpad-clear"
          >
            Clear
          </button>
        )}

        <button
          onClick={() => handlePress('0')}
          className="numpad-btn"
          data-testid="numpad-0"
        >
          0
        </button>

        <button
          onClick={handleBackspace}
          className="numpad-btn text-gray-500"
          data-testid="numpad-backspace"
        >
          <Delete className="w-6 h-6 mx-auto" />
        </button>
      </div>

      {/* Clear keeps its own row once the point has taken its place, so it never
          disappears from a pad someone is used to. */}
      {allowDecimal && (
        <button
          onClick={handleClear}
          className="w-full mt-2 h-12 rounded-xl bg-white text-gray-500 font-medium
                     active:scale-95 transition-transform"
          data-testid="numpad-clear"
        >
          Clear
        </button>
      )}

      {/* Confirm Button */}
      {onConfirm && (
        <button
          onClick={onConfirm}
          className="w-full mt-3 h-14 bg-[#8B0000] text-white text-xl font-semibold rounded-xl
                     active:scale-95 transition-transform"
          data-testid="numpad-confirm"
        >
          Confirm / पुष्टि गर्नुहोस्
        </button>
      )}
    </div>
  );
}
